import { createElement, type Ref } from 'react';
import { cloneDeep } from 'lodash-es';
import insane from 'insane';

export type HtmlProps<T extends HTMLElement = HTMLElement> = {
    source?: null | string;

    // Both are equivalents
    container?: keyof HTMLElementTagNameMap | ComponentType;
    children?: string;
    fwdRef?: Ref<T extends keyof HTMLElementTagNameMap ? HTMLElementTagNameMap[T] : T>;

    /**
     * Add classes to nested html elements.
     *  - All original tags are striped away.
     *
     *  - Too allow more that one nested tag modyfication,
     *   expected shape is array of tuples where:
     *      0: lowercase tag name
     *      1: className
     *
     *  - To skip some tag, omit second part
     *    or supply anything other than string.
     *
     *  - Example (white-space only for clarity):
     *      source:
     *          <span>one</span>
     *          <strong>two</strong>
     *          <span>three</span>
     *          <span>four</span>
     *
     *      classify:
     *          [
     *              ['span', 'red'],
     *              ['strong', 'green'],
     *              ['span'],
     *              ['span', 'blue']
     *          ]
     *
     *      output:
     *          <span class="red">one</span>
     *          <strong class="green">two</strong>
     *          <span>three</span>
     *          <span class="blue">four</span>
     */
    classify?: Array<[keyof HTMLElementTagNameMap, string]>;
    additionalTags?: Array<keyof HTMLElementTagNameMap>;
    additionalAttrs?: Array<[tagname: keyof HTMLElementTagNameMap, attr: string]>;

    className?: string;
    style?: CSSProperties;
};
const noop = () => {};

// Sanitizer config
const allowedAttrs = insane.defaults.allowedAttributes;
const tags = [
    'input',
    'a',
    'div',
    'p',
    'span',
    'img',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'ul',
    'ol',
    'li',
    'i',
    'em',
    'b',
    'strong',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'picture',
    'source',
];
tags.forEach(tag => {
    allowedAttrs[tag] = [
        ...(allowedAttrs[tag] || []),
        'class',
        'id',
        'name',
        'width',
        'height',
        'style',
        'rel',
        'media',
        'srcset',
        'value',
    ];
});

const defaultConfig = Object.assign({}, insane.defaults, {
    allowedTags: Array.from(new Set([...insane.defaults.allowedTags, 'wbr', ...tags])),
    allowedAttributes: Object.assign({}, insane.defaults.allowedAttributes, allowedAttrs),
});

function getConfig(
    additionalTags?: Array<keyof HTMLElementTagNameMap>,
    additionalAttrs?: Array<[keyof HTMLElementTagNameMap, string]>,
) {
    if (!additionalTags && !additionalAttrs) return defaultConfig;

    const config = cloneDeep(defaultConfig);
    if (additionalTags) {
        config.allowedTags = Array.from(new Set([...config.allowedTags, ...additionalTags]));
    }
    if (additionalAttrs) {
        additionalAttrs.forEach(([tagname, attr]) => {
            const prevAttrs: string[] = [...config.allowedAttributes[tagname]];
            config.allowedAttributes[tagname] = Array.from(new Set([...prevAttrs, attr]));
        });
    }
    return config;
}

export const cleanup = (d: Maybe<string>): string => insane(d, defaultConfig);

/** Generator for 'insane' filter option */
const createFilter = (classify: Array<[keyof HTMLElementTagNameMap, string]>): AnyFunction => {
    if (!Array.isArray(classify)) return noop;

    // Keep index of iterated tag name
    const iterationIndex: Dict = {};

    /**
     * Filter allows modifying attrs of iterated (allowed!) element.
     *  - 'attrs' contain all attributes (even those not allowed)
     *  - Since it's not used to filter here, true must be returned
     */
    return ({ tag, attrs }: { tag: string; attrs: { class?: string } }) => {
        let ii = iterationIndex[tag];
        ii = !Number.isInteger(ii) ? 0 : ++ii;

        // Keep the index
        iterationIndex[tag] = ii;

        // Get current tag modifications only
        const cTagMod = classify.filter(modTupple => Array.isArray(modTupple) && modTupple[0] === tag);

        // To class or not to class, that is the question?
        //  1. there are modifications for iterated tag name
        //  2. n-th modyfication exist in valid format
        //  3. ^ & has a className part set
        if (cTagMod.length && Array.isArray(cTagMod[ii]) && typeof cTagMod[ii][1] === 'string') {
            // Add classes
            attrs.class = cTagMod[ii][1]; // eslint-disable-line no-param-reassign
        }

        return true;
    };
};

export default function Html(props: HtmlProps) {
    const { source, children, container, classify, additionalTags, additionalAttrs, className, style, fwdRef } = props;
    const config = getConfig(additionalTags, additionalAttrs);
    const htmlCleanup = (d: Maybe<string>): string => insane(d, config);

    if (!source && !children) return null;
    // console.log({ source, config });
    // Add classes to nested tags
    if (classify) config.filter = createFilter(classify);

    // Sanitize input
    const clean = htmlCleanup(source || children);

    // Result props
    const resProps: { [index: string]: any } = {
        dangerouslySetInnerHTML: { __html: clean },
        ref: fwdRef,
        className,
        style,
    };

    // Children shall not pass when "dangerouslySetInnerHTML" is used
    delete resProps.children;

    return createElement(container || 'span', resProps);
}
